import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: number;
  user_id: string;
  username: string;
  email: string;
  phone?: string;
  role?: string;
  created_at: string;
  updated_at: string;
  membership?: {
    level: number;
    expire?: string;
    totalPoints: number;
    pointsUpdatedAt: string;
  };
  recentTransactions?: Array<{
    change_amount: number;
    reason?: string;
    metadata?: Record<string, any>;
    created_at: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 检查本地存储的认证信息
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        // 设置 axios 默认 headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (error) {
        console.error('解析存储的用户信息失败:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const userApiUrl = process.env.REACT_APP_USER_API || 'https://user.betaspace.top';
      const response = await fetch(`${userApiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const { user: userData, token: userToken } = data.data;
          
          setUser(userData);
          setToken(userToken);
          
          // 保存到本地存储
          localStorage.setItem('token', userToken);
          localStorage.setItem('user', JSON.stringify(userData));
          
          // 设置 axios 默认 headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
          
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('登录失败:', error);
      return false;
    }
  };

  const logout = () => {
    // 调用登出 API
    if (token) {
      const userApiUrl = process.env.REACT_APP_USER_API || 'https://user.betaspace.top';
      fetch(`${userApiUrl}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }).catch(console.error);
    }
    
    // 清除状态
    setUser(null);
    setToken(null);
    
    // 清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // 清除 axios headers
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      isAuthenticated
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 